import { isNodeSelection } from "@tiptap/core";

import { nodeIsImage } from "@/components/tiptap/Image";

import type { DocumentModel } from "@/models/DocumentModel";

export type BlockType =
  | "text"
  | "bulletList"
  | "orderedList"
  | "taskList"
  | "heading1"
  | "heading2"
  | "heading3"
  | "blockquote"
  | "callout"
  | "image"
  | "code"
  | "embed"
  | "horizontalRule"
  | "table"
  | "graph"
  | "plotlyChart";

export function appendBlock(doc: DocumentModel, blockTypeToAdd: BlockType, attrs?: object) {
  const { tiptap, editorState } = doc.editor;
  if (!tiptap) {
    return;
  }
  const { state } = tiptap;
  const { selection } = state;
  const currentPos = state.selection.$anchor.pos;
  let appendBlockIndex = state.selection.$anchor.after(1);
  let shouldDeleteCurrentBlock = false;

  if (selection.$head.parent.content.size === 0 && !editorState.isNonConvertableBlock) {
    // if caret in any other block
    // and block is empty
    // set shouldDeleteCurrentBlock to true
    shouldDeleteCurrentBlock = true;
  }

  if (
    isNodeSelection(selection) &&
    (nodeIsImage(selection.node) || selection.node?.type.name === "horizontalRule")
  ) {
    appendBlockIndex += 1;
  }

  // add new block
  switch (blockTypeToAdd) {
    case "text": {
      tiptap.chain().focus().insertContentAt(appendBlockIndex, "<p></p>").run();
      break;
    }
    case "bulletList": {
      tiptap.chain().focus().insertContentAt(appendBlockIndex, "<p></p>").toggleBulletList().run();
      break;
    }
    case "orderedList": {
      tiptap.chain().focus().insertContentAt(appendBlockIndex, "<p></p>").toggleOrderedList().run();
      break;
    }
    case "taskList": {
      tiptap.chain().focus().insertContentAt(appendBlockIndex, "<p></p>").toggleTaskList().run();
      break;
    }
    case "heading1": {
      tiptap
        .chain()
        .focus()
        .insertContentAt(appendBlockIndex, "<p></p>")
        .toggleHeading({ level: 1 })
        .run();
      break;
    }
    case "heading2": {
      tiptap
        .chain()
        .focus()
        .insertContentAt(appendBlockIndex, "<p></p>")
        .toggleHeading({ level: 2 })
        .run();
      break;
    }
    case "heading3": {
      tiptap
        .chain()
        .focus()
        .insertContentAt(appendBlockIndex, "<p></p>")
        .toggleHeading({ level: 3 })
        .run();
      break;
    }
    case "blockquote": {
      tiptap
        .chain()
        .focus()
        .insertContentAt(appendBlockIndex, "<p></p>")
        .unsetCallout()
        .toggleBlockquote()
        .run();
      break;
    }
    case "callout": {
      tiptap
        .chain()
        .focus()
        .insertContentAt(appendBlockIndex, "<p></p>")
        .unsetBlockquote()
        .toggleCallout()
        .run();
      break;
    }
    case "code": {
      tiptap.chain().focus().appendCodeBlock(appendBlockIndex).run();
      break;
    }
    case "image": {
      tiptap.chain().focus().appendImage(appendBlockIndex, attrs).run();
      break;
    }
    case "graph": {
      tiptap.chain().focus().appendGraph(appendBlockIndex, attrs).run();
      break;
    }
    case "plotlyChart": {
      tiptap.chain().focus().appendPlotlyChart(appendBlockIndex, attrs).run();
      break;
    }
    case "horizontalRule": {
      tiptap.chain().focus().insertContentAt(appendBlockIndex, "<hr /><p></p>").run();
      break;
    }
    case "table": {
      tiptap
        .chain()
        .focus()
        .insertContentAt(appendBlockIndex, "<p></p>")
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
      break;
    }
    case "embed": {
      const { url } = attrs as { url: string };
      tiptap.chain().focus().appendEmbed(appendBlockIndex, { url }).run();
      break;
    }
  }

  if (shouldDeleteCurrentBlock) {
    tiptap
      .chain()
      .deleteRange({ from: currentPos - 1, to: currentPos })
      .run();
  }
}
